import { Box, Center, Flex, Spinner } from "@chakra-ui/react";
import { EmptyMessage } from "@components/EmptyMessage";
import { OperationTileMultichain } from "@components/OperationTile";
import { ViewOverlay } from "@components/ViewOverlay/ViewOverlay";
import { type ImplicitAccount, normalizeTezosToMultichainOp } from "@umami/chains";
import { type Account } from "@umami/core";
import { useGetOperations } from "@umami/data-polling";
import { useCurrentAccount, useSelectedNetwork } from "@umami/state";

import { useBuyTokenFlow } from "@/hooks/useBuyTokenFlow";
import { trackButtonClick } from "@/utils/analytics";

const checkHasTezosAccount = (account?: ImplicitAccount) =>
  !!Object.keys(account?.chains ?? {}).find(chain => chain.startsWith("tezos"));

export const NormalActivityTab = () => {
  const currentAccount = useCurrentAccount();
  const { handleAssetSelectClick } = useBuyTokenFlow();
  const hasTezosAccount = checkHasTezosAccount(currentAccount);
  const { explorerUrl: tezosExplorerUrl, saplingContractAddress } = useSelectedNetwork();

  const { operations, isLoading, isFirstLoad, triggerRef } = useGetOperations(
    currentAccount ?? ({} as Account),
    hasTezosAccount
  );

  const isEmpty = operations.length === 0 && !isLoading;

  return (
    <>
      <Flex
        zIndex={1}
        flexDirection="column"
        flexGrow={1}
        padding={{
          md: "10px",
          base: "8px",
        }}
      >
        {isLoading && isFirstLoad && (
          <Center display="flex" height="100%" minHeight="136px">
            <Spinner />
          </Center>
        )}
        {isEmpty && (
          <EmptyMessage
            margin="auto"
            cta="Buy crypto"
            onClick={() => {
              trackButtonClick("buy", {
                button_label: "Buy crypto",
                section: "dashboard",
                action: "button_click",
                flow: "buy crypto",
              });

              return handleAssetSelectClick();
            }}
            title="Add crypto to your portfolio"
          />
        )}

        {operations.length > 0 && (
          <>
            <Box borderRadius="8px">
              {operations.map(operation => {
                const normalizedOperation =
                  "chainId" in operation
                    ? operation
                    : normalizeTezosToMultichainOp(
                        operation,
                        currentAccount?.address.pkh ?? "",
                        tezosExplorerUrl,
                        saplingContractAddress
                      );

                return (
                  <OperationTileMultichain
                    key={`${operation.id}-${operation.timestamp}-${
                      "txDirection" in operation ? operation.txDirection : ""
                    }`}
                    operation={normalizedOperation}
                  />
                );
              })}
              {/* trigger for loading more operations */}
            </Box>
            <Box ref={triggerRef} />
            <Center
              flexDirection="column"
              display={isLoading && !isFirstLoad ? "flex" : "none"}
              paddingY="30px"
            >
              <Spinner marginTop="30px" size="md" />
            </Center>
          </>
        )}
      </Flex>
      {isEmpty && <ViewOverlay iconType="activity" />}
    </>
  );
};
